import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from 'context/providers/ToastProvider';

/**
 * Hook personalizado para manejar la lógica del formulario de gastos
 */
const useGastoData = (selectedGasto, ultimoGasto) => {
    const { showErrorToast } = useToast();

    const [formData, setFormData] = useState({
        vDescripcion: "",
        idTipoGasto: "",
        idCatCuentaBancaria: "",
        idUsuario: "",
        idCatFormaPago: "",
        vResponsable: "",
        idGasto: 0,
        bHasNotaVenta: 0,
        DetallesCuenta: "",
        vFormaPago: "",
        vTipoGasto: "",
        dFechaGasto: "",
        idTipoMoneda: "1",
        dTipoCambio: 1,
        dCantidad: "",
        dPrecioUnitario: "",
        vTipoFactorIva: "",
        dTasaCuotaIva: 0.00,
        dIva: 0,
        dIvaRet: 0,
        dIsrRet: 0,
        dDescuento: 0,
        dImporte: 0,
        dTotal: 0
    });

    const [formErrors, setFormErrors] = useState({});
    const [date, setDate] = useState(null);
    const [notaDeVenta, setNotaDeVenta] = useState({
        file: null,
        fileName: ''
    });

    const [tipoDeCambio, setTipoDeCambio] = useState({
        USD_CAMBIADO: selectedGasto?.idTipoMoneda || 1,
    });

    const [tipoDeCambioTexto, setTipoDeCambioTexto] = useState("");

    /**
     * Maneja los cambios en los campos del formulario
     */
    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (value) {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                [field]: "",
            }));
        }
    };

    /**
     * Calcula los valores automáticamente
     */
    const calcularValores = () => {
        const cantidad = parseFloat(formData.dCantidad) || 0;
        const precioUnitario = parseFloat(formData.dPrecioUnitario) || 0;
        const descuento = parseFloat(formData.dDescuento) || 0;
        const dIsrRet = parseFloat(formData.dIsrRet) || 0;
        const dIvaRet = parseFloat(formData.dIvaRet) || 0;
        const tipoFactorIva = formData.vTipoFactorIva;
        const tasaCuotaIva = parseFloat(formData.dTasaCuotaIva) || 0.00;

        const tasaCuotaIvaFinal = tipoFactorIva === "Tasa" ? tasaCuotaIva : tipoFactorIva === "Cuota" ? tasaCuotaIva : 0;
        const importe = cantidad * precioUnitario;

        let iva = 0;
        if (tipoFactorIva === "Tasa") {
            iva = importe * tasaCuotaIvaFinal;
        } else if (tipoFactorIva === "Cuota") {
            iva = tasaCuotaIvaFinal;
        }

        const total = importe - descuento + iva - dIsrRet - dIvaRet;

        setFormData((prev) => ({
            ...prev,
            dImporte: importe,
            dIva: iva,
            dTotal: total,
        }));
    };

    /**
     * Obtiene el tipo de cambio
     */
    const obtenerTipoDeCambio = async () => {
        try {
            const response = await fetch(`https://api.exchangerate-api.com/v4/latest/USD`);
            const data = await response.json();

            if (data && data.rates && data.rates.MXN) {
                setTipoDeCambio({
                    USD: data.rates.MXN,
                    MXN: 1,
                    USD_CAMBIADO: selectedGasto?.idTipoMoneda === 2 && selectedGasto?.dTipoCambio !== undefined && selectedGasto?.dTipoCambio !== null
                        ? selectedGasto.dTipoCambio
                        : data.rates.MXN
                });
                setTipoDeCambioTexto(`1 USD = ${data.rates.MXN} MXN`);
            } else {
                showErrorToast('Datos de tipo de cambio no disponibles');
            }
        } catch (error) {
            showErrorToast("Error al obtener el tipo de cambio", error);
        }
    };

    /**
     * Valida los datos del formulario
     */
    const validarDatosGasto = () => {
        let newErrors = {};

        if (!formData.vDescripcion.trim()) newErrors.vDescripcion = "La descripción es obligatoria";
        if (!formData.idTipoGasto) newErrors.idTipoGasto = "Debes seleccionar un tipo de gasto";
        if (!formData.idCatCuentaBancaria) newErrors.idCatCuentaBancaria = "Debes seleccionar una cuenta bancaria";
        if (!formData.idCatFormaPago) newErrors.idCatFormaPago = "Debes seleccionar una forma de pago";
        if (!formData.dCantidad || isNaN(formData.dCantidad) || Number(formData.dCantidad) <= 0) newErrors.dCantidad = "Cantidad inválida";
        if (!formData.dPrecioUnitario || isNaN(formData.dPrecioUnitario) || Number(formData.dPrecioUnitario) < 0) newErrors.dPrecioUnitario = "Precio unitario inválido";
        if (!formData.dImporte || isNaN(formData.dImporte) || Number(formData.dImporte) < 0) newErrors.dImporte = "El importe debe ser mayor o igual a 0";
        if (!formData.idUsuario) newErrors.idUsuario = "El usuario es obligatorio";
        if (!formData.vTipoFactorIva?.trim()) newErrors.vTipoFactorIva = "El tipo de factor IVA es obligatorio";
        if (
           isNaN(formData.dTasaCuotaIva) || Number(formData.dTasaCuotaIva) < 0
        ) {
            newErrors.dTasaCuotaIva = "La tasa de cuota IVA debe ser mayor o igual a 0";
        }
        if (!date) {
            newErrors.date = "Debes seleccionar una fecha";
        }
       
        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Procesa el archivo de nota de venta
     */
    const procesarArchivo = (file, setArchivo) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const fechaActual = new Date();
            const opcionesFecha = { day: '2-digit', month: 'short', year: 'numeric' };
            const fechaFormateada = fechaActual.toLocaleDateString('es-ES', opcionesFecha)
                .replace(/\s+/g, '_');

            const totalFormateado = `$${formData.dTotal.toFixed(2)}`;
            const uuid = uuidv4();
            const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
            const nuevoNombreArchivo = `Gasto_${fechaFormateada}_${totalFormateado}_${uuid}${fileExtension}`;

            const base64String = event.target.result.split(",")[1];
            setArchivo({
                fileName: nuevoNombreArchivo,
                file: base64String
            });
        };

        reader.onerror = () => {
            showErrorToast("Error al leer el archivo.");
        };

        reader.readAsDataURL(file);
    };

    /**
     * Selecciona y valida el archivo de nota de pago
     */
    const seleccionarArchivoNotaPago = (e) => {
        const file = e.target.files[0];
        
        if (file) {
            const allowedExtensions = ['.png', '.jpg', '.pdf'];
            const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

            if (!allowedExtensions.includes(fileExtension)) {
                showErrorToast("Solo se permiten archivos con extensión .png, .jpg o .pdf");
                return;
            }


            if (file.size > 1 * 1024 * 1024) {
                showErrorToast("El archivo no debe pesar más de 1 MB.");
                return;
            }

            procesarArchivo(file, setNotaDeVenta);
        } else {
            showErrorToast("Debe seleccionar un archivo válido.");
        }
    };

    /**
     * Maneja el cambio de fecha
     */
    const handleDateChange = (selectedDate) => {
        setDate(selectedDate);
    };

    /**
     * Prepara los datos para enviar
     */
    const prepararDatosGasto = () => {
        return {
            idGasto: formData.idGasto,
            vDescripcion: formData.vDescripcion,
            idTipoGasto: formData.idTipoGasto,
            idCatCuentaBancaria: formData.idCatCuentaBancaria,
            idUsuario: formData.idUsuario,
            idCatFormaPago: formData.idCatFormaPago,
            vNotaVenta_Base64: notaDeVenta.file,
            vNotaVenta_Name: notaDeVenta.fileName,
            dFechaGasto: date,
            vMoneda: String(formData.idTipoMoneda) === "1" ? "MXN" : String(formData.idTipoMoneda) === "2" ? "USD" : "",
            dTipoCambio: formData.dTipoCambio || 1,
            dCantidad: formData.dCantidad,
            dPrecioUnitario: formData.dPrecioUnitario,
            vTipoFactorIva: formData.vTipoFactorIva,
            dTasaCuotaIva: formData.dTasaCuotaIva || 0,
            dIva: formData.dIva || 0,
            dIvaRet: formData.dIvaRet || 0,
            dIsrRet: formData.dIsrRet || 0,
            dDescuento: formData.dDescuento || 0,
            dImporte: formData.dImporte || 0,
            dTotal: formData.dTotal || 0,
        };
    };

    // Effect para cargar gasto seleccionado o último gasto
    useEffect(() => {
        if (selectedGasto && Object.keys(selectedGasto).length > 0) {
            setFormData(selectedGasto);
            setDate(new Date(selectedGasto.dFechaGasto));
        } else if (ultimoGasto) {
            setFormData((prevState) => ({
                ...prevState,
                idCatFormaPago: ultimoGasto.idCatFormaPago ?? prevState.idCatFormaPago,
                idTipoGasto: ultimoGasto.idTipoGasto ?? prevState.idTipoGasto,
                vTipoFactorIva: ultimoGasto.vTipoFactorIva ?? prevState.vTipoFactorIva
            }));
        }
    }, [selectedGasto, ultimoGasto]);

    // Effect para calcular valores
    useEffect(() => {
        calcularValores();
    }, [formData.dCantidad, formData.dPrecioUnitario, formData.dDescuento, formData.vTipoFactorIva, formData.dTasaCuotaIva, formData.dIvaRet, formData.dIsrRet]);

    return {
        formData,
        setFormData,
        formErrors,
        setFormErrors,
        date,
        setDate,
        notaDeVenta,
        setNotaDeVenta,
        tipoDeCambio,
        setTipoDeCambio,
        tipoDeCambioTexto,
        handleChange,
        handleDateChange,
        obtenerTipoDeCambio,
        validarDatosGasto,
        seleccionarArchivoNotaPago,
        prepararDatosGasto
    };
};

export default useGastoData;
